import type { FC } from "react"
import { Fragment, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import type { DateRefOption } from "helpers/hooks/useSelectOptions"
import type { DateReferenceGroup } from "types/date.types"
import ForecastChip from "./ForecastChip"
import { useCanScrollDown } from "./hooks"

const GROUP_LABELS: Record<DateReferenceGroup, string> = {
  hours: "dates.GROUP_HOURS",
  days: "dates.GROUP_DAYS",
  longer: "dates.GROUP_LONGER",
}
const GROUPS = Object.keys(GROUP_LABELS) as DateReferenceGroup[]

/** Keeps the rows clear of the scrollbar and lines the headers up with them. */
const ROW_PADDING_X = 2

interface DateReferenceRowProps {
  option: DateRefOption
  selected: boolean
  onSelect: (value: string) => void
}

const DateReferenceRow: FC<DateReferenceRowProps> = ({ option, selected, onSelect }) => {
  const { t } = useTranslation()

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(option.value)}
      sx={(theme) => ({
        gap: 1,
        px: ROW_PADDING_X,
        py: 0.5,
        // Finger-sized rows on a phone; on a desktop one height, with a chip or without.
        minHeight: { xs: 52, md: 36 },
        borderRadius: `${theme.shape.borderRadius}px`,
        // A primary tint marks the selected row, with no check to take up room.
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.16),
        },
      })}
    >
      <ListItemText
        primary={t(option.label)}
        primaryTypographyProps={{ noWrap: true }}
        sx={{ my: 0 }}
      />
      {option.forecastTime && <ForecastChip forecastTime={option.forecastTime} />}
      {/* The calendar opens beside the list; on a phone, in a step of its own. */}
      {option.value === "custom" && (
        <>
          <CalendarMonthOutlinedIcon
            fontSize="small"
            sx={{ display: { xs: "none", md: "block" }, color: "text.secondary" }}
          />
          <ChevronRightRoundedIcon
            fontSize="small"
            sx={{ display: { xs: "block", md: "none" }, color: "text.secondary" }}
          />
        </>
      )}
    </ListItemButton>
  )
}

interface DateReferenceListProps {
  options: DateRefOption[]
  selectedValue: string
  onSelect: (value: string) => void
  /** The range applied now, written out above the list. */
  appliedRange: string
}

const DateReferenceList: FC<DateReferenceListProps> = ({
  options,
  selectedValue,
  onSelect,
  appliedRange,
}) => {
  const { t } = useTranslation()
  const { scrollRef, canScrollDown } = useCanScrollDown()

  const groupedOptions = useMemo(
    () =>
      GROUPS.map((group) => ({
        group,
        groupOptions: options.filter((option) => option.group === group),
      })).filter(({ groupOptions }) => groupOptions.length > 0),
    [options],
  )
  const customOption = useMemo(
    () => options.find((option) => option.value === "custom"),
    [options],
  )

  return (
    <Box
      ref={scrollRef}
      sx={(theme) => ({
        position: "relative",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.palette.background.paper,
        // Fades the last visible rows while there is more list below them.
        "&::after": {
          content: '""',
          position: "sticky",
          bottom: 0,
          display: canScrollDown ? "block" : "none",
          height: 24,
          marginTop: -3,
          pointerEvents: "none",
          background: `linear-gradient(transparent, ${theme.palette.background.paper})`,
        },
      })}
    >
      <List sx={{ px: 1 }}>
        <Box component="li" sx={{ px: ROW_PADDING_X, pt: 1, pb: 1.5 }}>
          {/* The variant's own color, the same as the group headers. */}
          <Typography variant="overlineDense" component="p" sx={{ mb: 0.75 }}>
            {t("dates.SHOWING")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {appliedRange}
          </Typography>
        </Box>
        <Divider />
        {groupedOptions.map(({ group, groupOptions }) => (
          <Fragment key={group}>
            <ListSubheader
              sx={{
                px: ROW_PADDING_X,
                // Same group title as the entity settings details.
                typography: "overlineDense",
                pt: 1.5,
                pb: 1,
                backgroundColor: "background.paper",
              }}
            >
              {t(GROUP_LABELS[group])}
            </ListSubheader>
            {groupOptions.map((option) => (
              <DateReferenceRow
                key={option.value}
                option={option}
                selected={option.value === selectedValue}
                onSelect={onSelect}
              />
            ))}
          </Fragment>
        ))}
        {customOption && (
          <>
            <Divider sx={{ my: 1 }} />
            <DateReferenceRow
              option={customOption}
              selected={customOption.value === selectedValue}
              onSelect={onSelect}
            />
          </>
        )}
      </List>
    </Box>
  )
}

export default DateReferenceList
